import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { getAnnotations } from '@api/annotations';
import { annotationsFeatureSelector, supportedMetricsSelector } from '@store/common/selectors';
import { annotationAtom } from '@src/atoms/toggles';
import { parseAnnotations } from './utils';
import { pageMetaAtom } from '@src/atoms/pageMeta';

function useFetchAnnotations({ interval = {}, metric = null, fetchRawData = false }: any) {
  const isAnnotationsEnabled = useSelector(annotationsFeatureSelector);
  const annotationsToggled = useAtomValue(annotationAtom);
  const supportedMetrics: any = useSelector(supportedMetricsSelector);
  const validMetrics: any = Object.keys(supportedMetrics);
  const { flowPresetId } = useAtomValue(pageMetaAtom);

  // gracefully fail
  const defaultValue = metric || fetchRawData ? [] : {};

  // stale time prevents redundant calls from multiple subscribers
  // interval change or mutations will force a refetch
  const { data = defaultValue, dataUpdatedAt } = useQuery(
    ['annotations', interval],
    () => {
      return getAnnotations({ params: { from: interval.from, to: interval.to } });
    },
    {
      retry: 3,
      refetchOnWindowFocus: false,
      enabled: !!interval && isAnnotationsEnabled,
      staleTime: 5 * 60 * 1000, // 5m
      // notifyOnChangeProps: 'tracked',
      placeholderData: { data: { annotations: [] } },
      select: (response) => {
        if (annotationsToggled) {
          if (fetchRawData) {
            return response.data.annotations || [];
          } else {
            const annotations = parseAnnotations(response, validMetrics, !!flowPresetId);
            if (metric) {
              return metric in annotations ? annotations[metric] : [];
            } else {
              return annotations;
            }
          }
        }
        return defaultValue;
      },
      onError: (err) => {
        console.error('annotation error', err);
      },
    },
  );

  return { data, dataUpdatedAt };
}

export default useFetchAnnotations;
